/**
 * Fetch-boundary mock layer for the YaaS Client Console.
 *
 * `mockApiFetch` is invoked at the very top of the two client API wrappers
 * (`orgApiFetch` / `orgApiFetchVoid` and the private `walletApiFetch`) when
 * `isSimMode()` is true. It matches the request's `method` + query-stripped
 * `path` against the handler registry and, on a hit, returns mock data after
 * a small simulated latency. On a miss it returns `{ hit: false }` and the
 * caller proceeds to the real fetch.
 *
 * The FE stays byte-identical — only the data source swaps here.
 */

import type {
    Asset,
    ClientOperatingContextListView,
    ClientOperatingContextSummary,
    ClientPositionView,
    ComplianceModuleDefinition,
    ComplianceModuleKey,
    ComplianceRequirementListView,
    ComplianceRequirementView,
    ComplianceReviewView,
    CreatePositionInput,
    FundingSource,
    PositionSourceKindInput,
    Program,
    ProgramPosition,
    SavedDestination
} from "@solomon-apps/shared";
import { YaasPositionStatus } from "@solomon-apps/shared";
import type { MeResponse } from "@solomon-apps/shared/src/types/orgAuth";

import type { ClientDashboard } from "@/types/clientDashboard";
import type { ClientAssignedProgramView, CreateClientRunProgramInput } from "@/types/programCenteredYaas";

import type {
    AgreementEnvelopeStatus,
    AgreementEnvelopeView,
    AgreementType,
    ApplicationBundle,
    OnboardingStepView
} from "../clientOnboarding";
import type {
    ClientEarningsResponse,
    DistributionItem,
    MultisigPreflightResponse,
    MultisigsHistoryResponse
} from "../clientPositions";
import type { WalletDistributionSettings } from "../clientRewardsDestination";
import type { WalletView } from "../clientWallets";
import type { PaginatedResponse } from "../orgApiFetch";
import type { DiscoveredPosition } from "../positionDiscovery";

import {
    buildCreatedPosition,
    buildDiscoveredPositions,
    buildGoldUsdvDiscovery,
    buildGoldUsdvEnrolledPositions,
    buildMultisigPreflight,
    type GoldUsdvSimScenario,
    MOCK_CLIENT_DASHBOARD,
    MOCK_EARNINGS,
    MOCK_MULTISIGS_HISTORY,
    MOCK_PROGRAM_SURFACES,
    MOCK_SAVED_DESTINATIONS,
    SIM_GOLD_SURFACE_UUID,
    SIM_GOLD_WALLET_ADDRESS,
    SIM_USDV_MINT
} from "./mockData";
import { readSimReportingMode, simReportingFailure, simReportingHistory } from "./mockReporting";
import {
    cancelTeamMemberInvite,
    fixTeamMemberInvite,
    inviteTeamMember,
    listTeam,
    MockHttpError,
    patchTeamMember,
    regenerateTeamMemberClaim,
    resetTeamMemberPassword
} from "./mockTeam";
import { isSimMode } from "./simMode";

/** Simulated network latency (ms) so loading states still render. */
const MOCK_LATENCY_MS = 120;
const NOW = "2026-06-08T00:00:00.000Z";
const ISSUER_ORG_UUID = "issuer-org-mock";
const ISSUER_CLIENT_UUID = "issuer-client-mock";
const SECOND_ISSUER_ORG_UUID = "issuer-org-mock-atlas";
const SECOND_ISSUER_CLIENT_UUID = "issuer-client-mock-atlas";
const PENDING_ISSUER_ORG_UUID = "issuer-org-mock-pending";
const PENDING_ISSUER_CLIENT_UUID = "issuer-client-mock-pending";
const SUSPENDED_ISSUER_ORG_UUID = "issuer-org-mock-suspended";
const SUSPENDED_ISSUER_CLIENT_UUID = "issuer-client-mock-suspended";
const SELF_ISSUER_CLIENT_UUID = "issuer-client-mock-self";
const CLIENT_ORG_UUID = "client-org-mock";
const CLIENT_CONTROLLED_PROGRAM_UUID = "program-mock-client-controlled";
const END_USER_PARENT_PROGRAM_UUID = "program-mock-end-user-parent";
const END_USER_PROGRAM_SURFACE_UUID = "program-surface-mock-end-user";
const END_USER_SURFACE_UUID = "surface-mock-wallet-account";
const ASSET_USDV = "asset-usdv-mock";
const USDV_ASSET = {
    uuid: ASSET_USDV,
    symbol: "USDv",
    name: "USDv",
    mintAddress: "USDvMint111111111111111111111111111111",
    decimals: 9,
    status: "Active" as const
};

const CLIENT_SIM_SCENARIOS = [
    "one-context",
    "multi-context",
    "eligible-market-maker",
    "self-client",
    "hard-gate",
    "overlap-pending-review",
    "mixed-gold-usdv",
    "gold-usdv-only",
    "gold-usdv-unavailable"
] as const;

type ClientSimScenario = (typeof CLIENT_SIM_SCENARIOS)[number];

const CLIENT_SIM_SCENARIO_STORAGE_KEY = "yaas:client-sim-scenario";
const DEFAULT_CLIENT_SIM_SCENARIO: ClientSimScenario = "one-context";

function isClientSimScenario(value: string | null): value is ClientSimScenario {
    return Boolean(value && (CLIENT_SIM_SCENARIOS as readonly string[]).includes(value));
}

function readClientSimScenario(): ClientSimScenario {
    if (typeof window === "undefined") return DEFAULT_CLIENT_SIM_SCENARIO;

    const params = new URLSearchParams(window.location.search);
    const fromUrl = params.get("clientSimScenario") ?? params.get("simScenario");
    if (isClientSimScenario(fromUrl)) {
        try {
            window.sessionStorage.setItem(CLIENT_SIM_SCENARIO_STORAGE_KEY, fromUrl);
        } catch {
            // Scenario selection is a dev affordance; ignore blocked storage.
        }
        return fromUrl;
    }

    try {
        const stored = window.sessionStorage.getItem(CLIENT_SIM_SCENARIO_STORAGE_KEY);
        if (isClientSimScenario(stored)) return stored;
    } catch {
        // Fall through to the stable default.
    }

    return DEFAULT_CLIENT_SIM_SCENARIO;
}

export function readGoldUsdvSimScenario(): GoldUsdvSimScenario | null {
    const scenario = readClientSimScenario();
    return scenario === "mixed-gold-usdv" || scenario === "gold-usdv-only" || scenario === "gold-usdv-unavailable"
        ? scenario
        : null;
}

export interface MockHandlerContext {
    method: string;
    path: string;
    body: unknown;
    params: Record<string, string>;
}

export interface MockHandler {
    method: string;
    /** Returns captured params (possibly empty) on match, or null on miss. */
    match: (path: string) => Record<string, string> | null;
    respond: (ctx: MockHandlerContext) => unknown | Promise<unknown>;
}

/** Exact-path matcher — the query string is already stripped before this runs. */
function exact(target: string): (path: string) => Record<string, string> | null {
    return (path: string) => (path === target ? {} : null);
}

/** Single-segment capture matcher, e.g. `capture("/a/:uuid/b", "uuid")`. */
function capture(template: string, ...names: string[]): (path: string) => Record<string, string> | null {
    const pattern = new RegExp(`^${template.replace(/:[A-Za-z]+/g, "([^/]+)")}$`);
    return (path: string) => {
        const m = pattern.exec(path);
        if (!m) return null;
        return Object.fromEntries(names.map((n, i) => [n, m[i + 1] ?? ""]));
    };
}

function emptyPage<T>(): PaginatedResponse<T> {
    return { data: [], total: 0, limit: 100, offset: 0, page: 1 };
}

function pageOf<T>(items: T[]): PaginatedResponse<T> {
    return { data: items, total: items.length, limit: 100, offset: 0, page: 1 };
}

/**
 * In-memory store of positions created this session. The sim has no backend,
 * so a created position is kept here and returned by the GET list (newest
 * first) — that's what makes the positions table appear after the add-position
 * flow submits (otherwise the empty-state just re-renders). Resets on reload.
 */
async function ownershipChallenge(
    action: "add-position" | "edit-destination",
    subjectUuid: string,
    walletAddress: string,
    signerAddress?: string,
    sourceKind = "eoa",
    multisigProgramId?: string
): Promise<{ nonce: string; message: string; expiresAt: string; microTransferMemo: string }> {
    const signer = signerAddress ?? walletAddress;
    const nonce = Array.from(crypto.getRandomValues(new Uint8Array(32)), (byte) =>
        byte.toString(16).padStart(2, "0")
    ).join("");
    const payload = JSON.stringify([
        "client-position:v2",
        action,
        subjectUuid,
        ISSUER_CLIENT_UUID,
        walletAddress,
        signer,
        sourceKind,
        multisigProgramId ?? null
    ]);
    const digest = await crypto.subtle.digest("SHA-256", new TextEncoder().encode(payload));
    const hash = Array.from(new Uint8Array(digest), (byte) => byte.toString(16).padStart(2, "0")).join("");
    const expiresAt = new Date(Date.now() + 600_000).toISOString();
    return {
        nonce,
        expiresAt,
        microTransferMemo: `solomon-yaas:v1:${nonce}`,
        message: [
            `Solomon Platform Verify Wallet: ${walletAddress} for ${subjectUuid}`,
            "",
            ...(action === "add-position"
                ? ["Action: Add Position"]
                : ["Action: Edit Rewards Destination", `Wallet account: ${subjectUuid}`]),
            `Signer: ${signer}`,
            `Client context: ${ISSUER_CLIENT_UUID}`,
            `Request hash: ${hash}`,
            `Nonce: ${nonce}`,
            `Expires at: ${expiresAt}`
        ].join("\n")
    };
}

const createdPositions: ClientPositionView[] = [];

/**
 * Dev seed (data-layer only): `?sim=1&positions=N` pre-fills the table with N
 * sample positions so the populated / mobile layout can be worked on WITHOUT
 * running the add-position flow. No param → table stays empty (the add-position
 * empty-state walkthrough is unchanged). The FE is byte-identical either way;
 * this only changes what the GET list returns. `N` omitted (or non-numeric)
 * seeds all samples; otherwise the first N.
 */
let positionsSeeded = false;
/** Reward distributions seeded alongside positions so each position's
 *  Transactions table has rows (the FE filters them by surface name). */
const seededDistributions: DistributionItem[] = [];
function ensureSeededPositions(): void {
    if (positionsSeeded) return;
    if (typeof window === "undefined") return;
    const raw = new URLSearchParams(window.location.search).get("positions");
    if (raw === null) return; // no seed intent yet — let a later GET seed
    positionsSeeded = true;
    const samples = sampleSeedPositions();
    const n = parseInt(raw, 10);
    const count = Number.isFinite(n) && n > 0 ? Math.min(n, samples.length) : samples.length;
    createdPositions.push(...samples.slice(0, count));
    seededDistributions.push(...sampleDistributions());
}

/** Varied sample positions (different surfaces, statuses, amounts) so the
 *  seeded table reads like a real one — not N identical rows. Built on top of
 *  the shared `buildCreatedPosition` so the shape always matches the create
 *  flow's output. Each points at a different saved destination so the detail
 *  page's Rewards Destination resolves to a real name. */
function sampleSeedPositions(): ClientPositionView[] {
    const dest = (i: number): string => MOCK_SAVED_DESTINATIONS[i % MOCK_SAVED_DESTINATIONS.length].address;
    const make = (
        programSurfaceIndex: number,
        address: string,
        destAddress: string,
        label: string,
        status: YaasPositionStatus,
        manualUsdvAmount: string,
        totalRewards: string,
        sourceKind: PositionSourceKindInput = "eoa"
    ): ClientPositionView => ({
        ...buildCreatedPosition({
            programSurfaceUuid: MOCK_PROGRAM_SURFACES[programSurfaceIndex % MOCK_PROGRAM_SURFACES.length].uuid,
            walletAddress: address,
            rewardsDestinationAddress: destAddress,
            label,
            sourceKind
        }),
        status,
        manualUsdvAmount,
        totalRewards,
        verifiedAt: status === YaasPositionStatus.Pending ? null : "2026-06-09T00:00:00.000Z"
    });
    return [
        make(
            0,
            "7Np41oeYqPefeNQEHSv1UDhYrehxin3NStELkfyBgPMd",
            dest(0),
            "Treasury wallet",
            YaasPositionStatus.Active,
            "1250000",
            "8421.36"
        ),
        // Held by the sim wallet itself, so its Rewards Routing can be
        // verified and edited in the sim (Verify Ownership passes here).
        make(1, SIM_GOLD_WALLET_ADDRESS, dest(1), "Ops payout wallet", YaasPositionStatus.Active, "480000", "2190.07"),
        // Multisig source: the Address column should surface the vault PDA
        // (`usdvBalanceAddress`), not this multisig account address.
        make(
            2,
            "Mu1tisigMockAddre55Examp1eForSimMode00000000001",
            dest(2),
            "Squads treasury",
            YaasPositionStatus.Active,
            "640000",
            "3110.42",
            "multisig-squads"
        ),
        make(
            2,
            "GThUX1Mtg2T2VbqDmVr1H8K5w3Mtsg6Cv8mpQ4kXyz9",
            dest(2),
            "Growth fund",
            YaasPositionStatus.Pending,
            "320000",
            "0"
        ),
        make(
            0,
            "4Nd8sUq2wErTy6unoP1aSdFgHjKZxCvBnM3qWeRtY7u",
            dest(3),
            "Reserve wallet",
            YaasPositionStatus.Suspended,
            "95000",
            "612.18"
        ),
        // The default: rewards go back to the wallet itself, no saved entry.
        // Rewards Destinations lists it as Position Wallet.
        make(
            1,
            "B3sAwcD8Md4hVY3WK7bpdA14rM5SnSQUP3XfBsdTKEkc",
            "B3sAwcD8Md4hVY3WK7bpdA14rM5SnSQUP3XfBsdTKEkc",
            "",
            YaasPositionStatus.Active,
            "210000",
            "940.55"
        ),
        ...stagingMergedWalletPositions()
    ];
}

/** The wallet behind `stagingMergedWalletPositions`. */
const MERGED_WALLET_ADDRESS = "5ZWj7a1f8tWkjBESHKgrLmXshuXxqeY9SYcfbshpAqPG";
const MERGED_WALLET_ACCOUNT = {
    uuid: "account-mock-merged-wallet",
    label: "Main treasury",
    address: MERGED_WALLET_ADDRESS
};
const GOLD_USDV_POOL = "CpG1ZMHhK7iGKpvS1A6Ytf4hJbziY8xq1jKAX5GbfaZ2";
const USDC_USDV_POOL = "ESrLM3P7GUaLu6PhPjGvAs7LuMZHEcEa9oN6vV8r7mC4";

/**
 * The staging case: yaas-api creates one Program Position per (Program,
 * wallet), so one wallet targeted by three Programs comes back as three
 * Address positions (each on its own Program Surface), the same GOLD / USDv
 * Raydium LP position under two Programs, and a USDC / USDv LP position in a
 * second pool. The console merges them into three holdings: one Address row
 * and one row per LP position. Fixed uuids so the seeded distributions
 * below can reference them.
 */
function stagingMergedWalletPositions(): ClientPositionView[] {
    const base = (
        uuid: string,
        status: YaasPositionStatus,
        totalRewards: string,
        createdAt: string,
        programSurface: ClientPositionView["programSurface"]
    ): ClientPositionView => ({
        ...buildCreatedPosition({
            programSurfaceUuid: programSurface.uuid,
            walletAddress: MERGED_WALLET_ADDRESS,
            rewardsDestinationAddress: MOCK_SAVED_DESTINATIONS[0].address
        }),
        uuid,
        status,
        totalRewards,
        manualUsdvAmount: "0",
        verifiedAt: status === YaasPositionStatus.Pending ? null : "2026-06-09T00:00:00.000Z",
        programSurface,
        account: MERGED_WALLET_ACCOUNT,
        createdAt,
        updatedAt: createdAt
    });
    const address = (uuid: string, name: string): ClientPositionView["programSurface"] => ({
        uuid,
        name,
        type: "ADDRESS",
        surface: "Address",
        address: MERGED_WALLET_ADDRESS,
        trailingApyPct: null
    });
    const goldPool = {
        poolAddress: GOLD_USDV_POOL,
        positionAddress: "GoLdPersona1Position1111111111111111111111111",
        pairLabel: "GOLD / USDv",
        observedBalance: "18250.40"
    };
    // More of the wallet's positions in the same GOLD / USDv pool (separate
    // NFTs, own ranges): the console sums them into the one pool holding.
    // The last has no complete observation, so it is left out of the sum.
    const goldPool2 = {
        ...goldPool,
        positionAddress: "GoLdPersona1Position2222222222222222222222222",
        observedBalance: "27213.49"
    };
    const goldPool3 = {
        ...goldPool,
        positionAddress: "GoLdPersona1Position3333333333333333333333333",
        observedBalance: "49180.22"
    };
    const goldPoolUnobserved = {
        ...goldPool,
        positionAddress: "GoLdPersona1Position4444444444444444444444444",
        observedBalance: "0",
        completeness: "partial" as const
    };
    const usdcPool = {
        poolAddress: USDC_USDV_POOL,
        positionAddress: "J6243MWU1BZTJeAwQyQLb1LzPtoazZPoLmxvZVD9AGy1",
        pairLabel: "USDC / USDv",
        observedBalance: "6420.15"
    };
    // Mirrors yaas-api's client view: a Raydium row's Program Surface name and
    // product are the pool's pair label from Jachin's catalog.
    const lp = (
        uuid: string,
        status: YaasPositionStatus,
        totalRewards: string,
        createdAt: string,
        surfaceUuid: string,
        pool: typeof goldPool & { completeness?: "complete" | "partial" }
    ): ClientPositionView => ({
        ...base(uuid, status, totalRewards, createdAt, {
            uuid: surfaceUuid,
            name: pool.pairLabel,
            product: pool.pairLabel,
            type: "raydium-clmm",
            surface: "Raydium CLMM",
            address: pool.poolAddress,
            trailingApyPct: null
        }),
        lpPosition: {
            protocol: "raydium-clmm",
            poolAddress: pool.poolAddress,
            positionAddress: pool.positionAddress,
            observedAssetMint: "USDvMint111111111111111111111111111111111111",
            holdingOwnerAddress: MERGED_WALLET_ADDRESS,
            pairLabel: pool.pairLabel
        },
        observedBalance: pool.observedBalance,
        observation: {
            completeness: pool.completeness ?? "complete",
            solanaSlot: "452332586",
            observedAt: "2026-10-05T12:00:00.000Z",
            fetchedAt: "2026-10-05T12:00:05.000Z"
        }
    });
    return [
        base(
            "position-mock-merged-address-1",
            YaasPositionStatus.Active,
            "1204.18",
            "2026-05-02T00:00:00.000Z",
            address("surface-mock-program-a-address", "Solana Wallet Address")
        ),
        base(
            "position-mock-merged-address-2",
            YaasPositionStatus.Active,
            "388.90",
            "2026-06-11T00:00:00.000Z",
            address("surface-mock-program-b-address", "USDv Holder Rewards Address")
        ),
        base(
            "position-mock-merged-address-3",
            YaasPositionStatus.Pending,
            "0",
            "2026-09-20T00:00:00.000Z",
            address("surface-mock-program-c-address", "Partner Boost Wallet")
        ),
        lp(
            "position-mock-merged-lp-1",
            YaasPositionStatus.Active,
            "310.44",
            "2026-06-11T00:00:00.000Z",
            "surface-mock-program-b-raydium",
            goldPool
        ),
        lp(
            "position-mock-merged-lp-2",
            YaasPositionStatus.Suspended,
            "75.12",
            "2026-07-01T00:00:00.000Z",
            "surface-mock-program-c-raydium",
            goldPool
        ),
        lp(
            "position-mock-merged-lp-gold-2",
            YaasPositionStatus.Active,
            "142.08",
            "2026-07-15T00:00:00.000Z",
            "surface-mock-program-b-raydium",
            goldPool2
        ),
        lp(
            "position-mock-merged-lp-gold-3",
            YaasPositionStatus.Active,
            "201.66",
            "2026-08-20T00:00:00.000Z",
            "surface-mock-program-b-raydium",
            goldPool3
        ),
        lp(
            "position-mock-merged-lp-gold-4",
            YaasPositionStatus.Active,
            "0",
            "2026-09-02T00:00:00.000Z",
            "surface-mock-program-b-raydium",
            goldPoolUnobserved
        ),
        lp(
            "position-mock-merged-lp-3",
            YaasPositionStatus.Active,
            "96.30",
            "2026-08-04T00:00:00.000Z",
            "surface-mock-program-b-raydium",
            usdcPool
        )
    ];
}

/** Sample reward distributions for the seeded Program Surfaces — three per surface, varied
 *  dates/amounts/destinations, so the per-position Transactions table reads
 *  like a real distribution history. */
function sampleDistributions(): DistributionItem[] {
    const destAddrs = MOCK_SAVED_DESTINATIONS.map((d) => d.address);
    const out: DistributionItem[] = [];
    MOCK_PROGRAM_SURFACES.slice(0, 3).forEach((programSurface, pi) => {
        for (let i = 0; i < 3; i++) {
            const month = String(6 - i).padStart(2, "0");
            out.push({
                uuid: `dist-mock-${pi}-${i}`,
                distributionDate: `2026-${month}-14T00:00:00.000Z`,
                amountUsdv: 1200 * (pi + 1) + i * 213.45,
                pool: programSurface.name,
                sourceWallet: "So1omonYaaSDistributorVau1t111111111111111111",
                destinationWallet: destAddrs[(pi + i) % destAddrs.length],
                txSignature: `SimTx${pi}${i}Sig1111111111111111111111111111111111111111111111111111`
            });
        }
    });
    // The merged wallet's payouts, one per Program Position, so its holding's
    // Transactions table combines them.
    const merged: Array<[positionUuid: string, distributionDate: string, amountUsdv: number]> = [
        ["position-mock-merged-address-1", "2026-09-14T00:00:00.000Z", 402.11],
        ["position-mock-merged-address-2", "2026-09-14T00:00:00.000Z", 129.6],
        ["position-mock-merged-address-1", "2026-08-14T00:00:00.000Z", 398.42],
        ["position-mock-merged-lp-1", "2026-09-14T00:00:00.000Z", 103.48]
    ];
    merged.forEach(([positionUuid, distributionDate, amountUsdv], i) => {
        out.push({
            uuid: `dist-mock-merged-${i}`,
            distributionDate,
            amountUsdv,
            positionUuid,
            pool: "Solana Wallet Address",
            sourceWallet: "So1omonYaaSDistributorVau1t111111111111111111",
            destinationWallet: destAddrs[0],
            txSignature: `SimMergedTx${i}Sig111111111111111111111111111111111111111111111111111`
        });
    });
    return out;
}

const MOCK_CLIENT_ASSETS: Asset[] = [
    {
        ...USDV_ASSET,
        issuerOrganizationUuid: ISSUER_ORG_UUID,
        createdAt: NOW,
        updatedAt: NOW
    }
];
const MOCK_CLIENT_FUNDING_SOURCES: FundingSource[] = [
    {
        uuid: "client-funding-source-sim-1",
        ownerType: "IssuerClient",
        issuerOrganizationUuid: "issuer-org-mock",
        issuerClientUuid: "issuer-client-mock",
        assetUuid: ASSET_USDV,
        asset: USDV_ASSET,
        label: "Client Operating Treasury",
        address: "ClientTreasury11111111111111111111111111",
        managedBy: "Owner",
        sourceType: "direct",
        status: "Active",
        balance: "50000",
        lastDepositAt: null,
        totalDeposited: null,
        totalDistributed: null,
        metadata: null,
        createdAt: "2026-06-08T00:00:00.000Z",
        updatedAt: "2026-06-08T00:00:00.000Z"
    }
];
const createdClientRunPrograms: Program[] = [];

/**
 * Dev seed (data-layer only): `?sim=1&onboarding=<status>` returns the client
 * application in that state, so each onboarding screen can be looked at
 * without a backend. `draft` (or `rejected`) reaches the lite FORM — the one
 * screen that was otherwise unreachable in sim, because the default bundle is
 * approved and jumps straight to the celebration.
 *
 * Values: `draft` | `in_review` | `awaiting_agreements` | `rejected` |
 * `approved`. No param → approved, which is what SIM/E2E have always got, so
 * nothing existing changes.
 *
 * `awaiting_agreements` reaches the agreements-out screen — the issuer has
 * approved and the signing emails are with the authorized signatory. It is
 * the one state that needs envelopes to be worth looking at, so the bundle
 * carries them (see `simAgreementEnvelopes`); every other state sends none,
 * which is what the API does before approval.
 *
 * `&agreement=<status>` sets the envelope stage the screen reports. Values are
 * the `AgreementEnvelopeStatus` union — `created`, `sent`, `delivered`,
 * `client_signed`, `signed`, `declined`, `voided`, `email_bounced`. No param →
 * `sent`, the state a client sees first. Only read on `awaiting_agreements`.
 *
 * `&minting=1` adds the `minting_agreement_signing` step the issuer's Mint
 * and Redeem grant composes in, which is what makes the lite form ask for
 * the Authorized Mint & Redeem Addresses. Without it the sim client signs
 * the MSA only, like most clients. On `awaiting_agreements` it also puts the
 * second envelope on the screen, so the two-agreement layout is reachable.
 * `&onboardingPurpose=minting_only` renders the initial Market Maker profile;
 * `clientSimScenario=eligible-market-maker` is the separate post-upgrade
 * eligibility fixture.
 */
type SimOnboardingStatus = "draft" | "in_review" | "awaiting_agreements" | "rejected" | "approved";

function readSimOnboardingStatus(): SimOnboardingStatus {
    if (typeof window === "undefined") return "approved";
    const raw = new URLSearchParams(window.location.search).get("onboarding");
    return raw === "draft" || raw === "in_review" || raw === "awaiting_agreements" || raw === "rejected"
        ? raw
        : "approved";
}

const SIM_AGREEMENT_STATUSES: readonly AgreementEnvelopeStatus[] = [
    "created",
    "sent",
    "delivered",
    "client_signed",
    "signed",
    "declined",
    "voided",
    "email_bounced"
];

function readSimAgreementStatus(): AgreementEnvelopeStatus {
    if (typeof window === "undefined") return "sent";
    const raw = new URLSearchParams(window.location.search).get("agreement");
    return SIM_AGREEMENT_STATUSES.find((status) => status === raw) ?? "sent";
}

/**
 * One envelope, with the lifecycle timestamps the requested stage implies.
 * The screen prints these, so a stage whose timestamps disagreed with it
 * would read as a bug in the page rather than in the seed.
 */
function simAgreementEnvelope(agreementType: AgreementType, status: AgreementEnvelopeStatus): AgreementEnvelopeView {
    const reached = (...stages: AgreementEnvelopeStatus[]): string | null =>
        stages.includes(status) ? "2026-06-09T00:00:00.000Z" : null;

    return {
        uuid: `onboarding-agreement-sim-${agreementType}`,
        applicationUuid: "onboarding-application-sim-approved",
        stepUuid: `onboarding-step-sim-${agreementType}`,
        agreementType,
        status,
        // `created` is the only stage before the send, so everything from
        // `sent` onward carries a sent timestamp.
        sentAt: reached("sent", "delivered", "client_signed", "signed", "declined", "voided", "email_bounced"),
        deliveredAt: reached("delivered", "client_signed", "signed", "declined"),
        clientSignedAt: reached("client_signed", "signed"),
        issuerSignedAt: reached("signed"),
        declinedAt: reached("declined"),
        declinedReason:
            status === "declined"
                ? "The signatory asked for the notice period in section 9 to be changed before signing."
                : null,
        createdAt: NOW,
        updatedAt: NOW
    };
}

/**
 * The envelopes the issuer sent, mirroring `requiredAgreementTypes` in the
 * issuer's onboarding review model: the platform MSA unless this is a
 * mint-and-redeem-only client, plus the minting master whenever the client is
 * set up to mint.
 *
 * Both inputs are passed in rather than re-read, so the envelopes track the
 * same values the application does. Reading `&minting=1` directly would have
 * shown a Market Maker the minting step with no minting agreement beside it;
 * seeding the MSA unconditionally put a platform agreement in front of a
 * client whose `requestedServices` is `["mint_redeem"]` alone, contradicting
 * the page's own copy.
 */
function simAgreementEnvelopes(mintingEnabled: boolean, purpose: "platform" | "minting_only"): AgreementEnvelopeView[] {
    const status = readSimAgreementStatus();
    const envelopes: AgreementEnvelopeView[] = [];
    if (purpose !== "minting_only") envelopes.push(simAgreementEnvelope("msa", status));
    if (mintingEnabled) envelopes.push(simAgreementEnvelope("minting_master", status));
    return envelopes;
}

function readSimMintingEnabled(): boolean {
    if (typeof window === "undefined") return false;
    return new URLSearchParams(window.location.search).get("minting") === "1";
}

function readSimOnboardingPurpose(): "platform" | "minting_only" {
    if (typeof window === "undefined") return "platform";
    return new URLSearchParams(window.location.search).get("onboardingPurpose") === "minting_only"
        ? "minting_only"
        : "platform";
}

function simMintingStep(): OnboardingStepView {
    return {
        uuid: "onboarding-step-sim-minting",
        applicationUuid: "onboarding-application-sim-approved",
        stepName: "minting_agreement_signing",
        status: "not_started",
        payload: null,
        submittedAt: null,
        completedAt: null,
        rejectedAt: null,
        rejectionReason: null,
        createdAt: NOW,
        updatedAt: NOW
    };
}

function approvedOnboardingBundle(): ApplicationBundle {
    const status = readSimOnboardingStatus();
    const approved = status === "approved";
    const purpose = readSimOnboardingPurpose();
    const mintingEnabled = purpose === "minting_only" || readSimMintingEnabled();
    return {
        application: {
            uuid: "onboarding-application-sim-approved",
            clientOrganizationUuid: "client-org-mock",
            issuerOrganizationUuid: ISSUER_ORG_UUID,
            submittedByOrgUserUuid: "client-user-mock",
            status,
            purpose,
            requestedServices:
                purpose === "minting_only"
                    ? ["mint_redeem"]
                    : mintingEnabled
                      ? ["platform", "mint_redeem"]
                      : ["platform"],
            // A draft has never been submitted; the other states have.
            submittedAt: status === "draft" ? null : "2026-06-08T00:00:00.000Z",
            rejectionReason:
                status === "rejected"
                    ? "We need a clearer copy of the incorporation document — the scan was cut off at the bottom."
                    : null,
            signatoryEmail: "client-admin@solomonlabs.org",
            signatoryName: "Client Sim Admin",
            incorporationJurisdiction: "us",
            // Approved is already acknowledged so SIM/E2E lands straight in the
            // console (the gate skips the first-login welcome celebration).
            approvalAcknowledgedAt: approved ? NOW : null,
            // The lite form asks for the Terms of Service afresh on a draft.
            termsAcceptedAt: null,
            createdAt: NOW,
            updatedAt: NOW
        },
        steps: mintingEnabled ? [simMintingStep()] : [],
        // Envelopes exist only once the issuer has approved and sent them,
        // which in this seed is exactly `awaiting_agreements`.
        agreements: status === "awaiting_agreements" ? simAgreementEnvelopes(mintingEnabled, purpose) : [],
        liteOnboardingEnabled: false
    };
}

function clientOperatingContext(
    issuerClientUuid: string,
    issuerOrganizationUuid: string,
    issuerOrganizationName: string,
    overrides: Partial<ClientOperatingContextSummary> = {}
): ClientOperatingContextSummary {
    const context: ClientOperatingContextSummary = {
        issuerClientUuid,
        issuerOrganizationUuid,
        issuerOrganizationName,
        issuerCapabilityStatus: "Active",
        status: "Active",
        defaultable: issuerClientUuid === ISSUER_CLIENT_UUID,
        platformEligibility: "eligible",
        readOnly: false,
        readOnlyReason: null,
        isSelfClient: false,
        ...overrides
    };
    // Every sim relationship was granted Distributions and API Access; like
    // yaas-api, each is live only while Platform Access is.
    const platformLive =
        context.platformEligibility === "eligible" &&
        context.status === "Active" &&
        context.issuerCapabilityStatus === "Active";
    return {
        ...context,
        distributionsAccess: overrides.distributionsAccess ?? platformLive,
        apiAccess: overrides.apiAccess ?? platformLive
    };
}

function clientOperatingContexts(): ClientOperatingContextListView {
    const scenario = readClientSimScenario();
    const initialMintingOnly = readSimOnboardingPurpose() === "minting_only";
    const initialOnboardingPolicy = initialMintingOnly
        ? { platformEligibility: "not_eligible" as const }
        : { platformEligibility: "eligible" as const };
    if (scenario === "eligible-market-maker") {
        return {
            contexts: [
                clientOperatingContext(ISSUER_CLIENT_UUID, ISSUER_ORG_UUID, "Solomon Labs", {
                    defaultable: true,
                    platformEligibility: "eligible"
                })
            ],
            defaultContextUuid: ISSUER_CLIENT_UUID
        };
    }
    if (scenario === "self-client") {
        return {
            contexts: [
                clientOperatingContext(SELF_ISSUER_CLIENT_UUID, CLIENT_ORG_UUID, "Client Sim Treasury", {
                    defaultable: true,
                    isSelfClient: true
                })
            ],
            defaultContextUuid: SELF_ISSUER_CLIENT_UUID
        };
    }

    if (scenario === "multi-context") {
        const contexts: ClientOperatingContextSummary[] = [
            clientOperatingContext(ISSUER_CLIENT_UUID, ISSUER_ORG_UUID, "Solomon Labs", { defaultable: true }),
            clientOperatingContext(SECOND_ISSUER_CLIENT_UUID, SECOND_ISSUER_ORG_UUID, "Atlas Issuer"),
            clientOperatingContext(PENDING_ISSUER_CLIENT_UUID, PENDING_ISSUER_ORG_UUID, "Pending Issuer", {
                status: "Pending",
                platformEligibility: "onboarding"
            }),
            clientOperatingContext(SUSPENDED_ISSUER_CLIENT_UUID, SUSPENDED_ISSUER_ORG_UUID, "Suspended Issuer", {
                status: "Suspended",
                readOnly: true,
                readOnlyReason: "IssuerClientSuspended"
            })
        ];

        return {
            contexts,
            defaultContextUuid: ISSUER_CLIENT_UUID
        };
    }

    return {
        contexts: [
            clientOperatingContext(ISSUER_CLIENT_UUID, ISSUER_ORG_UUID, "Solomon Labs", {
                defaultable: true,
                ...initialOnboardingPolicy
            })
        ],
        defaultContextUuid: ISSUER_CLIENT_UUID
    };
}

/**
 * The signed-in identity sim mode presents, built from the same operating
 * contexts the rest of this file serves so the session and the data can never
 * describe different orgs. `initSimSession` seeds the org auth store with it.
 *
 * Every field `needsOrgProfileRefresh` inspects is present and populated. That
 * matters: a partial `me` makes `useOrgAuth` fall through to the real
 * `fetchMe`, which has no backend to answer it in sim, and the rejection path
 * clears the session and bounces to `/login`.
 */
export function simMeResponse(): MeResponse {
    const { contexts, defaultContextUuid } = clientOperatingContexts();
    return {
        user: {
            uuid: "client-user-mock",
            email: "client-admin@solomonlabs.org",
            fullName: "Client Sim Admin"
        },
        org: {
            uuid: CLIENT_ORG_UUID,
            slug: "client-org-mock",
            name: "Client Sim Org",
            legalName: "Client Sim Org LLC",
            orgType: "Client"
        },
        orgRole: "Owner",
        // A client-org session: it operates as a client of an issuer and holds
        // no issuer capability of its own.
        issuerOrg: null,
        issuerCapability: null,
        canActAsIssuer: false,
        issuerCapabilityStatus: null,
        availableClientContexts: contexts,
        defaultClientContextUuid: defaultContextUuid
    };
}

function complianceModule(
    key: ComplianceModuleKey,
    displayName: string,
    overrides: Partial<ComplianceModuleDefinition> = {}
): ComplianceModuleDefinition {
    return {
        key,
        displayName,
        description: `${displayName} evidence requested by the issuer.`,
        applicability: "issuer_client",
        defaultGate: "hard",
        defaultSelectedForIssuerClient: true,
        moduleVersion: 1,
        evidenceSchemaVersion: 1,
        onboardingStepName: key,
        onboardingStepKind: "applicant_driven",
        submissionMechanic: "applicant_payload",
        payloadSchema: {},
        payloadFields: [],
        documentSlots: [],
        routePath: key.replaceAll("_", "-"),
        requires: [],
        notes: [],
        ...overrides
    };
}

function complianceRequirement(
    key: ComplianceModuleKey,
    displayName: string,
    options: {
        gate?: ComplianceRequirementView["gate"];
        approvedVersion?: number;
        pendingReview?: boolean;
        rejectedReview?: boolean;
        warningOnly?: boolean;
    } = {}
): ComplianceRequirementView {
    const gate = options.gate ?? (options.warningOnly ? "soft" : "hard");
    const approvedEvidenceVersion = options.approvedVersion
        ? {
              uuid: `${key}-evidence-version-${options.approvedVersion}`,
              evidenceUuid: `${key}-evidence`,
              versionNumber: options.approvedVersion,
              moduleKey: key,
              moduleVersion: 1,
              evidenceSchemaVersion: 1,
              status: "submitted" as const,
              payload: {},
              submittedByOrgUserUuid: "client-user-mock",
              submittedAt: "2026-06-08T00:00:00.000Z",
              createdAt: "2026-06-08T00:00:00.000Z",
              updatedAt: "2026-06-08T00:00:00.000Z",
              documents: []
          }
        : null;
    const reviewStatus: ComplianceReviewView["status"] | null = options.rejectedReview
        ? "rejected"
        : options.pendingReview
          ? "pending"
          : null;
    const pendingReviews = reviewStatus
        ? [
              {
                  uuid: `${key}-review`,
                  requirementUuid: `${key}-requirement`,
                  evidenceVersionUuid: approvedEvidenceVersion?.uuid ?? `${key}-submitted-version`,
                  reviewerType: "issuer" as const,
                  reviewerOrganizationUuid: ISSUER_ORG_UUID,
                  reviewedByOrgUserUuid: null,
                  status: reviewStatus,
                  reviewNote: reviewStatus === "rejected" ? "Please refresh the submitted evidence." : null,
                  reviewedAt: reviewStatus === "rejected" ? "2026-06-10T00:00:00.000Z" : null,
                  createdAt: "2026-06-08T00:00:00.000Z",
                  updatedAt: "2026-06-10T00:00:00.000Z"
              }
          ]
        : [];
    const satisfied = Boolean(approvedEvidenceVersion);
    const warningOnly = gate === "soft";

    return {
        uuid: `${key}-requirement`,
        ownerType: "issuer_client",
        issuerClientUuid: ISSUER_CLIENT_UUID,
        issuerCapabilityUuid: null,
        subjectOrganizationUuid: CLIENT_ORG_UUID,
        moduleKey: key,
        moduleVersion: 1,
        evidenceSchemaVersion: 1,
        gate,
        metadata: null,
        removedAt: null,
        module: complianceModule(key, displayName),
        approvedEvidenceVersion,
        pendingReviews,
        satisfied,
        blocksClientWrites: !satisfied && !warningOnly,
        warningOnly,
        blockReason: !satisfied && !warningOnly ? "HardRequirementUnsatisfied" : null,
        createdAt: "2026-06-08T00:00:00.000Z",
        updatedAt: "2026-06-10T00:00:00.000Z"
    };
}

function emptyComplianceRequirements(): ComplianceRequirementListView {
    return {
        requirements: [],
        summary: {
            requirementCount: 0,
            satisfiedCount: 0,
            missingCount: 0,
            pendingReviewCount: 0,
            blocksClientWrites: false,
            blockedRequirementUuids: [],
            warningRequirementUuids: []
        }
    };
}

function hardGateComplianceRequirements(): ComplianceRequirementListView {
    const requirements = [
        complianceRequirement("legal_entity", "Legal Entity"),
        complianceRequirement("use_case_and_intent", "Use Case And Intent", {
            pendingReview: true
        }),
        complianceRequirement("tax_identification", "Tax Identification", {
            approvedVersion: 1
        }),
        complianceRequirement("source_of_funds", "Source Of Funds", {
            approvedVersion: 1,
            pendingReview: true
        })
    ];
    const satisfiedCount = requirements.filter((requirement) => requirement.satisfied).length;

    return {
        requirements,
        summary: {
            requirementCount: requirements.length,
            satisfiedCount,
            missingCount: requirements.length - satisfiedCount,
            pendingReviewCount: requirements.reduce(
                (count, requirement) => count + requirement.pendingReviews.length,
                0
            ),
            blocksClientWrites: requirements.some((requirement) => requirement.blocksClientWrites),
            blockedRequirementUuids: requirements
                .filter((requirement) => requirement.blocksClientWrites)
                .map((requirement) => requirement.uuid),
            warningRequirementUuids: requirements
                .filter((requirement) => requirement.warningOnly)
                .map((requirement) => requirement.uuid)
        }
    };
}

function overlapPendingReviewRequirements(): ComplianceRequirementListView {
    const requirements = [
        complianceRequirement("legal_entity", "Legal Entity", {
            pendingReview: true
        }),
        complianceRequirement("tax_identification", "Tax Identification", {
            pendingReview: true
        }),
        complianceRequirement("source_of_funds", "Source Of Funds", {
            pendingReview: true
        })
    ];

    return {
        requirements,
        summary: {
            requirementCount: requirements.length,
            satisfiedCount: 0,
            missingCount: requirements.length,
            pendingReviewCount: requirements.length,
            blocksClientWrites: true,
            blockedRequirementUuids: requirements.map((requirement) => requirement.uuid),
            warningRequirementUuids: []
        }
    };
}

function complianceRequirements(): ComplianceRequirementListView {
    const scenario = readClientSimScenario();
    if (scenario === "hard-gate") {
        return hardGateComplianceRequirements();
    }
    if (scenario === "overlap-pending-review") {
        return overlapPendingReviewRequirements();
    }
    return emptyComplianceRequirements();
}

function signerAddressOf(body: unknown): string | null {
    const b = (body ?? {}) as { signerAddress?: unknown };
    return typeof b.signerAddress === "string" ? b.signerAddress : null;
}

function asCreateProgramPositionInput(body: unknown): CreatePositionInput {
    const b = (body ?? {}) as {
        programSurfaceUuid?: unknown;
        walletAddress?: unknown;
        rewardsDestinationAddress?: unknown;
        label?: unknown;
        verificationMode?: unknown;
        manualReviewDescription?: unknown;
        sourceKind?: unknown;
        signerAddress?: unknown;
        multisigProgramId?: unknown;
    };
    return {
        programSurfaceUuid: typeof b.programSurfaceUuid === "string" ? b.programSurfaceUuid : "",
        walletAddress: typeof b.walletAddress === "string" ? b.walletAddress : "",
        ...(typeof b.rewardsDestinationAddress === "string"
            ? { rewardsDestinationAddress: b.rewardsDestinationAddress }
            : {}),
        ...(typeof b.label === "string" || b.label === null ? { label: b.label } : {}),
        ...(b.verificationMode === "direct-solomon" ? { verificationMode: b.verificationMode } : {}),
        ...(typeof b.manualReviewDescription === "string"
            ? { manualReviewDescription: b.manualReviewDescription }
            : {}),
        ...(b.sourceKind === "eoa" || b.sourceKind === "multisig-squads" ? { sourceKind: b.sourceKind } : {}),
        ...(typeof b.signerAddress === "string" ? { signerAddress: b.signerAddress } : {}),
        ...(typeof b.multisigProgramId === "string" ? { multisigProgramId: b.multisigProgramId } : {})
    };
}

function asCreateClientRunProgramInput(body: unknown): CreateClientRunProgramInput {
    const b = (body ?? {}) as Partial<CreateClientRunProgramInput>;
    return {
        parentProgramUuid: typeof b.parentProgramUuid === "string" ? b.parentProgramUuid : "",
        name: typeof b.name === "string" && b.name.trim() ? b.name : "Client Sim Program",
        description:
            typeof b.description === "string" || b.description === null
                ? b.description
                : "Client-run incentive program",
        status: b.status,
        metadata:
            b.metadata && typeof b.metadata === "object" && !Array.isArray(b.metadata)
                ? (b.metadata as Record<string, unknown>)
                : null
    };
}

function asProgramStatus(value: unknown): Program["status"] {
    return value === "Draft" || value === "Paused" || value === "Ended" ? value : "Active";
}

function clientControlledProgramView(): ClientAssignedProgramView {
    return {
        program: {
            uuid: CLIENT_CONTROLLED_PROGRAM_UUID,
            issuerOrganizationUuid: ISSUER_ORG_UUID,
            issuerClientUuid: null,
            parentProgramUuid: null,
            runType: "IssuerRun",
            participationModel: "ClientControlledPositions",
            assignmentScope: "AllIssuerClients",
            status: "Active",
            rewardDistributionMode: "Automatic",
            latestConfigVersionNumber: 0,
            target: null,
            payoutModel: null,
            distributionSettings: null,
            pinnedPolicySetRevisionUuid: null,
            name: "Solomon Client-Controlled Positions",
            description: "Program surfaces assigned to this client for position reporting.",
            metadata: null,
            createdAt: NOW,
            updatedAt: NOW
        },
        assignment: {
            uuid: "assignment-mock-client-controlled",
            programUuid: CLIENT_CONTROLLED_PROGRAM_UUID,
            issuerClientUuid: ISSUER_CLIENT_UUID,
            status: "Active",
            metadata: null,
            createdAt: NOW,
            updatedAt: NOW
        },
        surfaces: MOCK_PROGRAM_SURFACES.map((programSurfaceOption) => ({
            programSurface: {
                uuid: programSurfaceOption.uuid,
                programUuid: CLIENT_CONTROLLED_PROGRAM_UUID,
                surfaceUuid: `surface-${programSurfaceOption.uuid}`,
                status: "Active",
                metadata: null,
                createdAt: NOW,
                updatedAt: NOW
            },
            surface: {
                uuid: `surface-${programSurfaceOption.uuid}`,
                issuerOrganizationUuid: ISSUER_ORG_UUID,
                surfaceCategoryUuid: "surface-category-client-controlled",
                surfaceTypeUuid: programSurfaceOption.yaasProtocol.uuid,
                assetUuid: ASSET_USDV,
                asset: USDV_ASSET,
                name: programSurfaceOption.displayName,
                description: null,
                observationSources: { poolAddress: programSurfaceOption.address },
                status: "Active",
                metadata: null,
                createdAt: NOW,
                updatedAt: NOW
            },
            surfaceCategory: {
                uuid: "surface-category-client-controlled",
                slug: "client-controlled-positions",
                name: "Client-Controlled Positions",
                description: null,
                observationSources: ["poolAddress"],
                participationMode: "ClientControlledPositions",
                status: "Active",
                metadata: null,
                createdAt: NOW,
                updatedAt: NOW
            },
            surfaceType: {
                uuid: programSurfaceOption.yaasProtocol.uuid,
                surfaceCategoryUuid: "surface-category-client-controlled",
                slug: programSurfaceOption.yaasProtocol.slug,
                name: programSurfaceOption.name,
                providerName: programSurfaceOption.yaasProtocol.name,
                description: null,
                status: "Active",
                metadata: null,
                createdAt: NOW,
                updatedAt: NOW
            }
        })),
        effectivePolicies: [],
        positions: createdPositions.map((position) => ({
            uuid: position.uuid,
            programSurfaceUuid: position.programSurface.uuid,
            participantAccountUuid: position.account.uuid,
            status: position.status as ProgramPosition["status"],
            manualUsdvAmount: position.manualUsdvAmount,
            totalRewards: position.totalRewards,
            externalPositionId: null,
            positionSourceKind: "IssuerClientManaged",
            positionControlKind: "IssuerClient",
            metadata: null,
            createdAt: position.createdAt,
            updatedAt: position.updatedAt
        })),
        canCreateProgramPosition: true,
        canCreateClientRunProgram: false,
        clientAddedFundingSourcesAllowed: false
    };
}

function endUserParentProgramView(): ClientAssignedProgramView {
    return {
        program: {
            uuid: END_USER_PARENT_PROGRAM_UUID,
            issuerOrganizationUuid: ISSUER_ORG_UUID,
            issuerClientUuid: null,
            parentProgramUuid: null,
            runType: "IssuerRun",
            participationModel: "ClientEndUserEnrollments",
            assignmentScope: "AllIssuerClients",
            status: "Active",
            rewardDistributionMode: "Automatic",
            latestConfigVersionNumber: 0,
            target: null,
            payoutModel: null,
            distributionSettings: null,
            pinnedPolicySetRevisionUuid: null,
            name: "Solomon End-User Rewards",
            description: "Assigned parent Program that lets this client launch user rewards.",
            metadata: {
                regions: ["global"],
                builderConfig: {
                    participationModel: "ClientEndUserEnrollments",
                    clientAddedFundingSourcesAllowed: true,
                    programType: "continuous",
                    periodDays: 90,
                    baseApy: 6.5,
                    rewardsPct: 100,
                    settlementCycleLength: 30,
                    settlementCycleUnit: "days"
                }
            },
            createdAt: NOW,
            updatedAt: NOW
        },
        assignment: {
            uuid: "assignment-mock-end-user-parent",
            programUuid: END_USER_PARENT_PROGRAM_UUID,
            issuerClientUuid: ISSUER_CLIENT_UUID,
            status: "Active",
            metadata: null,
            createdAt: NOW,
            updatedAt: NOW
        },
        surfaces: [
            {
                programSurface: {
                    uuid: END_USER_PROGRAM_SURFACE_UUID,
                    programUuid: END_USER_PARENT_PROGRAM_UUID,
                    surfaceUuid: END_USER_SURFACE_UUID,
                    status: "Active",
                    metadata: null,
                    createdAt: NOW,
                    updatedAt: NOW
                },
                surface: {
                    uuid: END_USER_SURFACE_UUID,
                    issuerOrganizationUuid: ISSUER_ORG_UUID,
                    surfaceCategoryUuid: "surface-category-end-user-account",
                    surfaceTypeUuid: "surface-type-wallet-account",
                    assetUuid: ASSET_USDV,
                    asset: USDV_ASSET,
                    name: "Address",
                    description: "Address Surface assigned for client end-user rewards.",
                    observationSources: {},
                    status: "Active",
                    metadata: null,
                    createdAt: NOW,
                    updatedAt: NOW
                },
                surfaceCategory: {
                    uuid: "surface-category-end-user-account",
                    slug: "address",
                    name: "Address",
                    description: null,
                    observationSources: [],
                    participationMode: "ClientEndUserEnrollments",
                    status: "Active",
                    metadata: null,
                    createdAt: NOW,
                    updatedAt: NOW
                },
                surfaceType: {
                    uuid: "surface-type-wallet-account",
                    surfaceCategoryUuid: "surface-category-end-user-account",
                    slug: "wallet-account",
                    name: "Address",
                    providerName: "Solomon Labs",
                    description: null,
                    status: "Active",
                    metadata: null,
                    createdAt: NOW,
                    updatedAt: NOW
                }
            }
        ],
        effectivePolicies: [],
        positions: [],
        canCreateProgramPosition: false,
        canCreateClientRunProgram: true,
        clientAddedFundingSourcesAllowed: true,
        effectiveFundingSources: MOCK_CLIENT_FUNDING_SOURCES
    };
}

function clientRunProgramView(program: Program): ClientAssignedProgramView {
    return {
        program,
        assignment: null,
        surfaces: [],
        effectivePolicies: [],
        positions: [],
        canCreateProgramPosition: false,
        canCreateClientRunProgram: false,
        clientAddedFundingSourcesAllowed: true,
        effectiveFundingSources: MOCK_CLIENT_FUNDING_SOURCES
    };
}

/**
 * Seed client-run programs so the Programs console has a populated,
 * varied list in sim mode. The two base views are IssuerRun (positions /
 * enrollment) and get filtered out by `isClientRunProgramView`, so
 * without these the Programs rail is empty. Statuses span the filter
 * tabs (Active / Ended / Draft).
 */
function seedClientRunProgram(index: number, name: string, status: Program["status"], description: string): Program {
    const now = new Date().toISOString();
    return {
        uuid: `client-run-program-seed-${index}`,
        issuerOrganizationUuid: ISSUER_ORG_UUID,
        issuerClientUuid: ISSUER_CLIENT_UUID,
        parentProgramUuid: END_USER_PARENT_PROGRAM_UUID,
        runType: "ClientRun",
        participationModel: "ClientEndUserEnrollments",
        assignmentScope: null,
        status,
        rewardDistributionMode: "Automatic",
        latestConfigVersionNumber: 0,
        target: null,
        payoutModel: null,
        distributionSettings: null,
        pinnedPolicySetRevisionUuid: null,
        name,
        description,
        metadata: null,
        createdAt: now,
        updatedAt: now
    };
}

const SEED_CLIENT_RUN_PROGRAMS: Program[] = [
    seedClientRunProgram(1, "USDv Cashback Rewards", "Active", "Cashback on USDv spend, streamed to holders."),
    seedClientRunProgram(2, "Q3 Merchant Incentive", "Active", "Merchant acquisition incentive for Q3."),
    seedClientRunProgram(3, "Loyalty Yield Boost", "Active", "Boosted yield for loyalty-tier members."),
    seedClientRunProgram(4, "Referral Bonus", "Active", "Rewards for referring new depositors."),
    seedClientRunProgram(5, "Holiday Campaign 2025", "Ended", "Seasonal rewards campaign, now concluded."),
    seedClientRunProgram(6, "Beta Launch Rewards", "Ended", "Early-adopter rewards from beta launch."),
    seedClientRunProgram(7, "Spring Promo", "Draft", "Draft promo — not yet activated."),
    seedClientRunProgram(8, "Partner Pilot", "Draft", "Draft pilot with a launch partner.")
];

/**
 * Dev seed (data layer only): `?sim=1&programs=none` returns no assigned
 * Programs, for the Positions page's "Account Being Finalized" state. No param
 * keeps the usual assignments.
 */
function readSimNoPrograms(): boolean {
    if (typeof window === "undefined") return false;
    return new URLSearchParams(window.location.search).get("programs") === "none";
}

function clientProgramViews(): ClientAssignedProgramView[] {
    if (readSimNoPrograms()) return [];
    if (readGoldUsdvSimScenario()) return [goldUsdvProgramView()];
    return [
        clientControlledProgramView(),
        endUserParentProgramView(),
        ...SEED_CLIENT_RUN_PROGRAMS.map(clientRunProgramView),
        ...createdClientRunPrograms.map(clientRunProgramView)
    ];
}

function goldUsdvProgramView(): ClientAssignedProgramView {
    const view = endUserParentProgramView();
    const addressSurface = view.surfaces[0];
    const goldSurface = {
        ...addressSurface,
        programSurface: {
            ...addressSurface.programSurface,
            uuid: SIM_GOLD_SURFACE_UUID,
            surfaceUuid: "surface-mock-raydium-clmm"
        },
        surface: {
            ...addressSurface.surface,
            uuid: "surface-mock-raydium-clmm",
            surfaceCategoryUuid: "surface-category-mock-liquidity-pool",
            surfaceTypeUuid: "surface-type-mock-raydium-clmm",
            name: "Raydium CLMM",
            asset: { ...USDV_ASSET, mintAddress: SIM_USDV_MINT, decimals: 6 },
            observationSources: {}
        },
        surfaceCategory: {
            ...addressSurface.surfaceCategory,
            uuid: "surface-category-mock-liquidity-pool",
            slug: "liquidity-pool",
            name: "Liquidity Pool"
        },
        surfaceType: {
            ...addressSurface.surfaceType,
            uuid: "surface-type-mock-raydium-clmm",
            surfaceCategoryUuid: "surface-category-mock-liquidity-pool",
            slug: "raydium-clmm",
            name: "Raydium CLMM",
            providerName: "Raydium"
        }
    };
    return {
        ...view,
        program: { ...view.program, name: "Retail USDv Rewards", latestConfigVersionNumber: 1 },
        surfaces: [
            {
                ...addressSurface,
                surface: {
                    ...addressSurface.surface,
                    asset: { ...USDV_ASSET, mintAddress: SIM_USDV_MINT, decimals: 6 }
                }
            },
            goldSurface
        ],
        canEnrollWallet: true,
        canCreateClientRunProgram: false,
        canCreateProgramPosition: false
    };
}

function createClientRunProgramFromBody(body: unknown): Program {
    const input = asCreateClientRunProgramInput(body);
    const now = new Date().toISOString();
    const program: Program = {
        uuid: `client-run-program-sim-${createdClientRunPrograms.length + 1}`,
        issuerOrganizationUuid: ISSUER_ORG_UUID,
        issuerClientUuid: ISSUER_CLIENT_UUID,
        parentProgramUuid: input.parentProgramUuid || END_USER_PARENT_PROGRAM_UUID,
        runType: "ClientRun",
        participationModel: "ClientEndUserEnrollments",
        assignmentScope: null,
        status: asProgramStatus(input.status),
        rewardDistributionMode: "Automatic",
        latestConfigVersionNumber: 0,
        target: null,
        payoutModel: null,
        distributionSettings: null,
        pinnedPolicySetRevisionUuid: null,
        name: input.name,
        description: input.description ?? "Client-run incentive program",
        metadata: input.metadata ?? null,
        createdAt: now,
        updatedAt: now
    };
    createdClientRunPrograms.unshift(program);
    return program;
}

function programPositionFromCreated(position: ClientPositionView): ProgramPosition {
    return {
        uuid: position.uuid,
        programSurfaceUuid: position.programSurface.uuid,
        participantAccountUuid: position.account.uuid,
        status: position.status as ProgramPosition["status"],
        manualUsdvAmount: position.manualUsdvAmount,
        totalRewards: position.totalRewards,
        externalPositionId: null,
        positionSourceKind: "IssuerClientManaged",
        positionControlKind: "IssuerClient",
        metadata:
            position.verificationMode === "direct-solomon"
                ? {
                      verificationMode: position.verificationMode,
                      manualReviewDescription: position.manualReviewDescription,
                      manualReviewStatus: "pending"
                  }
                : null,
        createdAt: position.createdAt,
        updatedAt: position.updatedAt
    };
}

const HANDLERS: MockHandler[] = [
    {
        method: "GET",
        match: exact("/api/org/client/contexts"),
        respond: (): ClientOperatingContextListView => clientOperatingContexts()
    },
    {
        method: "GET",
        match: exact("/api/org/client/compliance/requirements"),
        respond: (): ComplianceRequirementListView => complianceRequirements()
    },
    {
        method: "GET",
        match: exact("/api/org/client/onboarding/applications"),
        respond: (): PaginatedResponse<ApplicationBundle> => pageOf([approvedOnboardingBundle()])
    },
    {
        // The lite form lists this to caption a formation document read back
        // from a prior save. The sim application has never saved one, so the
        // list is empty; an upload in sim never leaves the page.
        method: "GET",
        match: capture("/api/org/client/onboarding/applications/:uuid/documents", "uuid"),
        respond: (): { documents: never[] } => ({ documents: [] })
    },
    {
        // The lite approval celebration POSTs this on mount. With no handler
        // the sim threw and the celebration rendered its error/retry state —
        // a fixture gap that read as a product bug on screen. The sim bundle
        // is already acknowledged, so `acknowledgedNow` is false.
        method: "POST",
        match: capture("/api/org/client/onboarding/applications/:uuid/acknowledge-approval", "uuid"),
        respond: (): {
            bundle: ApplicationBundle;
            acknowledgedNow: boolean;
        } => ({ bundle: approvedOnboardingBundle(), acknowledgedNow: false })
    },
    {
        method: "GET",
        match: exact("/api/org/client/programs"),
        respond: (): PaginatedResponse<ClientAssignedProgramView> => pageOf(clientProgramViews())
    },
    {
        method: "POST",
        match: exact("/api/org/client/programs"),
        respond: (ctx): Program => createClientRunProgramFromBody(ctx.body)
    },
    {
        method: "GET",
        match: exact("/api/org/client/funding-source-inventory"),
        respond: (): PaginatedResponse<FundingSource> => pageOf(MOCK_CLIENT_FUNDING_SOURCES)
    },
    {
        method: "GET",
        match: exact("/api/org/client/assets"),
        respond: (): PaginatedResponse<Asset> => pageOf(MOCK_CLIENT_ASSETS)
    },
    {
        method: "GET",
        match: exact("/api/org/client/positions"),
        respond: (): PaginatedResponse<ClientPositionView> => {
            ensureSeededPositions();
            return pageOf(createdPositions);
        }
    },
    {
        method: "GET",
        match: exact("/api/org/client/saved-destinations"),
        respond: (): { data: SavedDestination[] } => ({
            data: MOCK_SAVED_DESTINATIONS
        })
    },
    {
        method: "GET",
        match: exact("/api/org/client/wallets"),
        respond: (): PaginatedResponse<WalletView> => emptyPage<WalletView>()
    },
    {
        method: "GET",
        match: exact("/api/org/client/dashboard"),
        respond: (): ClientDashboard => MOCK_CLIENT_DASHBOARD
    },
    {
        method: "GET",
        match: exact("/api/org/client/earnings"),
        respond: (): ClientEarningsResponse => {
            const mode = readSimReportingMode();
            if (mode === "error") throw simReportingFailure();
            return mode === "history" ? simReportingHistory().earnings : MOCK_EARNINGS;
        }
    },
    {
        method: "GET",
        match: exact("/api/org/client/distributions"),
        respond: (): PaginatedResponse<DistributionItem> => {
            const mode = readSimReportingMode();
            if (mode === "error") throw simReportingFailure();
            if (mode === "history") return pageOf(simReportingHistory().distributions);
            ensureSeededPositions();
            return pageOf(seededDistributions);
        }
    },
    {
        method: "POST",
        match: exact("/api/org/client/positions/challenge"),
        respond: async (ctx): ReturnType<typeof ownershipChallenge> => {
            const input = ctx.body as {
                walletAddress: string;
                signerAddress?: string;
                programSurfaceUuid: string;
                sourceKind?: string;
                multisigProgramId?: string;
            };
            return ownershipChallenge(
                "add-position",
                input.programSurfaceUuid,
                input.walletAddress,
                input.signerAddress,
                input.sourceKind,
                input.multisigProgramId
            );
        }
    },
    {
        method: "POST",
        match: capture("/api/org/client/positions/:uuid/destination-challenge", "uuid"),
        respond: async (ctx): ReturnType<typeof ownershipChallenge> => {
            ensureSeededPositions();
            const position = createdPositions.find((row) => row.uuid === ctx.params.uuid);
            if (!position) throw new Error("Position not found");
            return ownershipChallenge(
                "edit-destination",
                position.account.uuid,
                position.account.address,
                signerAddressOf(ctx.body) ?? undefined,
                position.sourceKind ?? "eoa",
                position.multisigProgramId ?? undefined
            );
        }
    },
    {
        method: "PATCH",
        match: capture("/api/org/client/positions/:uuid", "uuid"),
        respond: (ctx): ClientPositionView => {
            ensureSeededPositions();
            const input = ctx.body as {
                programPositionUuids: string[];
                rewardsDestinationAddress: string;
                nonce?: string;
                verificationSignature?: string;
            };
            const primary = createdPositions.find((row) => row.uuid === ctx.params.uuid);
            const selected = createdPositions.filter((row) => input.programPositionUuids.includes(row.uuid));
            if (
                !primary ||
                !input.nonce ||
                !input.verificationSignature ||
                !input.programPositionUuids.includes(primary.uuid) ||
                selected.length !== input.programPositionUuids.length ||
                selected.some((row) => row.account.uuid !== primary.account.uuid)
            )
                throw new Error("Invalid position destination change");
            for (const row of selected) row.rewardsDestinationAddress = input.rewardsDestinationAddress;
            return primary;
        }
    },
    {
        method: "POST",
        match: capture("/api/org/client/wallets/:accountUuid/rewards-destination/challenge", "accountUuid"),
        respond: async (ctx): ReturnType<typeof ownershipChallenge> => {
            ensureSeededPositions();
            const position = createdPositions.find((row) => row.account.uuid === ctx.params.accountUuid);
            if (!position) throw new Error("Wallet not found");
            return ownershipChallenge(
                "edit-destination",
                position.account.uuid,
                position.account.address,
                signerAddressOf(ctx.body) ?? undefined,
                position.sourceKind ?? "eoa",
                position.multisigProgramId ?? undefined
            );
        }
    },
    {
        method: "PUT",
        match: capture("/api/org/client/wallets/:accountUuid/rewards-destination", "accountUuid"),
        respond: (ctx): WalletDistributionSettings => {
            ensureSeededPositions();
            const input = ctx.body as {
                rewardsDestinationAddress: string;
                verificationNonce?: string;
                verificationSignature?: string;
            };
            if (!input.verificationNonce || !input.verificationSignature)
                throw new Error("Fresh wallet ownership proof required");
            const positions = createdPositions.filter((row) => row.account.uuid === ctx.params.accountUuid);
            if (!positions.length) throw new Error("Wallet not found");
            for (const position of positions) position.rewardsDestinationAddress = input.rewardsDestinationAddress;
            return {
                uuid: ctx.params.accountUuid,
                address: positions[0].account.address,
                rewardsDestinationAddress: input.rewardsDestinationAddress,
                distributionMode: null,
                distributionSettingsVersion: 0,
                distributionSettingsChangedAt: null,
                appliesTo: "future_runs"
            };
        }
    },
    {
        // Create (register) a position — mirrors POST /org/client/positions on
        // the backend, which responds in the program-position (admin) shape.
        method: "POST",
        match: exact("/api/org/client/positions"),
        respond: (ctx): ProgramPosition => {
            const input = asCreateProgramPositionInput(ctx.body);
            const goldScenario = readGoldUsdvSimScenario();
            if (goldScenario && input.verificationMode !== "direct-solomon") {
                if (createdPositions.some((position) => position.account.address === input.walletAddress))
                    throw new Error("Wallet already registered for this Program");
                const positions = buildGoldUsdvEnrolledPositions(input, goldScenario);
                createdPositions.unshift(...positions);
                return programPositionFromCreated(positions[0]);
            }
            const walletPositions = createdPositions.filter((row) => row.account.address === input.walletAddress);
            const currentDestination = walletPositions[0]?.rewardsDestinationAddress;
            // Like the API: an existing wallet keeps its destination; it
            // changes only through the wallet rewards destination endpoint.
            if (
                currentDestination &&
                input.rewardsDestinationAddress &&
                input.rewardsDestinationAddress !== currentDestination
            )
                throw new Error("This wallet already has a rewards destination. Change it from the wallet's settings.");
            const position = buildCreatedPosition({
                ...input,
                rewardsDestinationAddress: input.rewardsDestinationAddress ?? currentDestination ?? input.walletAddress
            });
            if (walletPositions[0]) position.account = walletPositions[0].account;
            if (goldScenario) {
                position.programSurface = {
                    uuid: input.programSurfaceUuid,
                    name: "Address",
                    product: "USDv",
                    surface: "Address",
                    type: "wallet-account",
                    address: input.walletAddress,
                    trailingApyPct: null
                };
            }
            createdPositions.unshift(position);
            return programPositionFromCreated(position);
        }
    },
    {
        method: "POST",
        match: exact("/api/org/client/positions/discover"),
        // One matched wallet-account holding bound to the end-user Address
        // surface — enough to walk the whole discovery flow (find → verify →
        // register) offline. Mirrors the mocked preflight's every-source-is-
        // the-mock-multisig classification.
        respond: (ctx): { positions: DiscoveredPosition[] } => {
            const scenario = readGoldUsdvSimScenario();
            const body = ctx.body as { address?: string } | null;
            return scenario
                ? buildGoldUsdvDiscovery(
                      END_USER_PROGRAM_SURFACE_UUID,
                      body?.address ?? SIM_GOLD_WALLET_ADDRESS,
                      scenario
                  )
                : buildDiscoveredPositions(END_USER_PROGRAM_SURFACE_UUID);
        }
    },
    {
        method: "POST",
        match: exact("/api/org/client/positions/multisig-preflight"),
        respond: (ctx): MultisigPreflightResponse =>
            readGoldUsdvSimScenario() ? { status: "not-found" } : buildMultisigPreflight(signerAddressOf(ctx.body))
    },
    {
        method: "POST",
        match: exact("/api/org/client/positions/multisigs-history"),
        respond: (): MultisigsHistoryResponse => MOCK_MULTISIGS_HISTORY
    },
    // Team (`/team`). These go through the shared `orgAuthFetch`, not
    // `orgApiFetch`, so they are reached via `resolveOrgAuthMock` below.
    {
        method: "GET",
        match: exact("/api/org/console/members"),
        respond: () => listTeam()
    },
    {
        method: "POST",
        match: exact("/api/org/console/members"),
        respond: (ctx) => inviteTeamMember(ctx.body)
    },
    {
        method: "PUT",
        match: capture("/api/org/console/members/:uuid", "uuid"),
        respond: (ctx) => patchTeamMember(ctx.params.uuid ?? "", ctx.body)
    },
    {
        method: "POST",
        match: capture("/api/org/console/members/:uuid/reset-password", "uuid"),
        respond: (ctx) => resetTeamMemberPassword(ctx.params.uuid ?? "")
    },
    {
        method: "POST",
        match: capture("/api/org/console/members/:uuid/regenerate-claim", "uuid"),
        respond: (ctx) => regenerateTeamMemberClaim(ctx.params.uuid ?? "")
    },
    {
        method: "POST",
        match: capture("/api/org/console/members/:uuid/fix-invite", "uuid"),
        respond: (ctx) => fixTeamMemberInvite(ctx.params.uuid ?? "", ctx.body)
    },
    {
        method: "POST",
        match: capture("/api/org/console/members/:uuid/cancel-invite", "uuid"),
        respond: (ctx) => cancelTeamMemberInvite(ctx.params.uuid ?? "")
    }
];

function delay(ms: number): Promise<void> {
    return new Promise((resolve) => {
        setTimeout(resolve, ms);
    });
}

function parseBody(init?: RequestInit): unknown {
    const raw = init?.body;
    if (typeof raw !== "string") return raw ?? null;
    try {
        return JSON.parse(raw) as unknown;
    } catch {
        return raw;
    }
}

export async function mockApiFetch(
    method: string,
    path: string,
    init?: RequestInit
): Promise<{ hit: true; value: unknown } | { hit: false }> {
    const pathWithoutQuery = path.split("?")[0];
    const upperMethod = method.toUpperCase();

    for (const handler of HANDLERS) {
        if (handler.method !== upperMethod) continue;
        const params = handler.match(pathWithoutQuery);
        if (params === null) continue;

        await delay(MOCK_LATENCY_MS);
        const value = await handler.respond({
            method: upperMethod,
            path: pathWithoutQuery,
            body: parseBody(init),
            params
        });
        return { hit: true, value };
    }

    return { hit: false };
}

/**
 * The same registry, answered at the shared `orgAuthFetch` boundary (via
 * `setOrgAuthFetchInterceptor`, see `register.ts`). Shared services such as
 * the org-members client call `orgAuthFetch` directly, so they never pass
 * through `orgApiFetch`'s sim check. Returns a `Response` on a hit, including
 * a handler's `MockHttpError` as its HTTP status and body, and `null` on a
 * miss or with sim mode off, so the live request proceeds untouched.
 */
export async function resolveOrgAuthMock(input: string | URL, init: RequestInit | undefined): Promise<Response | null> {
    if (!isSimMode()) return null;
    let path: string;
    try {
        path = new URL(input.toString(), window.location.origin).pathname;
    } catch {
        return null;
    }
    try {
        const mock = await mockApiFetch(init?.method ?? "GET", path, init);
        if (!mock.hit) return null;
        return new Response(JSON.stringify(mock.value ?? {}), {
            status: 200,
            headers: { "Content-Type": "application/json" }
        });
    } catch (error) {
        if (error instanceof MockHttpError) {
            return new Response(JSON.stringify(error.body), {
                status: error.status,
                headers: { "Content-Type": "application/json" }
            });
        }
        throw error;
    }
}
